import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { message, Modal } from "antd";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { generateRandomString } from "utils/helper";
import * as services from "../service/settingEmailTemplateApi";
import { setIsLoading, setEmailTemplateList } from "../slices/settingEmailTemplateSlice";

function toParams(params = {}) {
    const { pagination = {}, sortBy = [] } = params;
    return {
        pagination: { page: pagination.page, size: pagination.pageSize },
        sortBy: sortBy
            .filter((s) => s.order !== undefined)
            .map((s) => ({
                direction: s.order === "descend" ? "desc" : "asc",
                property: s.field,
            })),
    };
}

function useSettingEmailTemplate() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isLoading = useSelector((state) => state.settingEmailTemplate.isLoading);
    const currentCompany = useSelector((state) => state.company.currentCompany);
    const currentEmployee = useSelector((state) => state.company.currentEmployee);
    const emailTemplateList = useSelector((state) => state.settingEmailTemplate.emailTemplateList);
    const [pagination, setPagination] = useState({ page: 1, size: 10 });

    const getEmailTemplateList = useCallback(
        async (params = {}) => {
            try {
                dispatch(setIsLoading(true));
                const response = await services.getEmailTemplateList({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params.pagination,
                    sortBy: params.sortBy,
                    ...params,
                });
                dispatch(setEmailTemplateList(response.data?.content?.content || response.data?.content || []));
                setPagination({ ...pagination, total: response.data?.content?.totalElements });
            } catch (error) {
                message.error(t("common.error"));
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, pagination, dispatch]
    );

    const handleOnChange = useCallback(
        (tablePagination, tableFilters, tableSorter) => {
            setPagination(tablePagination);
            const modifiedSorter = Array.isArray(tableSorter) ? [...tableSorter] : [tableSorter];
            const modifiedParams = toParams({
                pagination: { page: tablePagination.current - 1, pageSize: tablePagination.pageSize },
                sortBy: modifiedSorter,
            });
            getEmailTemplateList(modifiedParams);
        },
        [getEmailTemplateList]
    );

    const handleOnStatus = useCallback(
        async (checked, record) => {
            try {
                dispatch(setIsLoading(true));
                await services.saveEmailTemplate({
                    id: record.id,
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    statusFlag: checked ? "ACTIVE" : "INACTIVE",
                    actionId: currentEmployee,
                });
                getEmailTemplateList({ pagination: { page: pagination.page - 1, size: pagination.size } });
                message.success(t("common.success"));
            } catch (error) {
                message.error(t("common.error"));
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, currentEmployee, pagination, dispatch, getEmailTemplateList]
    );

    const deleteEmailTemplate = useCallback(
        async (record) => {
            try {
                dispatch(setIsLoading(true));
                await services.deleteEmailTemplate({
                    id: record.id,
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    actionId: currentEmployee,
                });
                message.success(t("common.success"));
                getEmailTemplateList({ pagination: { page: 0, size: 10 } });
            } catch (error) {
                message.error(t("common.error"));
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, currentEmployee, dispatch, getEmailTemplateList]
    );

    const handleDelete = useCallback(
        (record) => {
            Modal.confirm({
                title: t("dialog.confirmation.header"),
                content: <p>{t("dialog.confirmation.delete").replace("{entity}", t("setting_email_template_list.entity"))}</p>,
                onOk() {
                    deleteEmailTemplate(record);
                },
                okText: t("common.confirm").toUpperCase(),
                cancelText: t("common.cancel").toUpperCase(),
            });
        },
        [deleteEmailTemplate, t]
    );

    const onCreate = useCallback(() => {
        navigate("/masterdata/setting_email_template/create");
    }, [navigate]);

    const onEdit = useCallback(
        (record) => {
            navigate(`/masterdata/setting_email_template/${record.id}/edit`);
        },
        [navigate]
    );

    const onSearch = useCallback(
        (values) => {
            getEmailTemplateList({ ...values, pagination: { page: 0, size: 10 } });
        },
        [getEmailTemplateList]
    );

    useEffect(() => {
        if (typeof currentCompany !== "object" && currentCompany !== null) {
            getEmailTemplateList({ pagination: { page: 0, size: 10 } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCompany]);

    return {
        emailTemplateList,
        isLoading,
        pagination,
        onChange: handleOnChange,
        onStatus: handleOnStatus,
        onDelete: handleDelete,
        onCreate,
        onEdit,
        onSearch,
    };
}

export default useSettingEmailTemplate;